import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useABCXYZAnalysis } from "./hooks/useABCXYZAnalysis";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import AnalysisTypeToggle from "./components/AnalysisTypeToggle";
import ABCXYZKpiCards from "./components/ABCXYZKpiCards";
import ABCXYZMatrix from "./components/ABCXYZMatrix";
import CrossMatrix from "./components/CrossMatrix";
import ObjectConfigPanel from "./components/ObjectConfigPanel";
import ABCXYZAnalysisSettings from "./ABCXYZAnalysisSettings";
import {
  ANALYSIS_TYPE_CROSS,
  ANALYSIS_TYPE_LABELS,
  ANALYSIS_TYPE_OBJECT,
  ANALYSIS_TYPE_VARIANT,
  COUNT_METRIC,
  DEFAULT_TITLE,
} from "./constants";

/**
 * ABC/XYZ Matrix dashboard tile.
 *
 * An overview and prioritization dashboard over process variants or business
 * object groups: an analysis-type toggle, KPI cards, the 3x3
 * impact × stability matrix and a ranked drill-down table. All
 * classification is computed by the backend /abc-xyz endpoints; the Variant
 * Explorer remains the variant-level detail table and is untouched.
 */
const ABCXYZAnalysisPanel = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const {
    loading,
    error,
    variantSummary,
    objectSummary,
    crossSummary,
    configOptions,
    objectTypes,
    title,
    analysisType,
    variantObjectType,
    variantMode,
    objectType,
    groupByAttribute,
    abcMetric,
    abcAggregation,
    dateField,
    dateFieldOrigin,
    xyzBasis,
    xyzGranularity,
    abcPeriod,
    fiscalYearStartMonth,
    objectConfigCollapsed,
    setTitle,
    setAnalysisType,
    setVariantObjectType,
    setVariantMode,
    setObjectType,
    setGroupByAttribute,
    setAbcMetric,
    setAbcAggregation,
    setDateField,
    setDateFieldOrigin,
    setXyzBasis,
    setXyzGranularity,
    setFiscalYearStartMonth,
    setAbcPeriod,
    setObjectConfigCollapsed,
    fetchConfigOptions,
    fetchVariantSummary,
    fetchObjectSummary,
    fetchCrossSummary,
  } = useABCXYZAnalysis(activeFile, initialSettings);

  const { filterState } = useGlobalFilters();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const isFirstFilterRenderRef = useRef(true);

  const appliedObjectConfigRef = useRef(null);
  const buildObjectSnapshot = useCallback(
    () => ({
      objectType,
      groupByAttribute,
      abcMetric,
      abcAggregation,
      dateField,
      dateFieldOrigin,
      xyzBasis,
      xyzGranularity,
      abcPeriod,
      fiscalYearStartMonth,
    }),
    [
      objectType,
      groupByAttribute,
      abcMetric,
      abcAggregation,
      dateField,
      dateFieldOrigin,
      xyzBasis,
      xyzGranularity,
      abcPeriod,
      fiscalYearStartMonth,
    ],
  );
  // Collapsing the configuration once a result exists is what makes the
  // results fit: the expanded panel is ~120px, almost exactly the amount by
  // which the applied grid overflows the tile's default height. Growing the
  // tile instead would leave that space empty in Variant mode, which has no
  // inline panel. Only collapse on success — a failed Apply must keep the
  // controls visible so the user can correct the configuration.
  const applyObjectSummary = useCallback(async () => {
    const snapshot = buildObjectSnapshot();
    appliedObjectConfigRef.current = snapshot;
    const ok = await fetchObjectSummary(snapshot);
    if (ok) setObjectConfigCollapsed(true);
    return ok;
  }, [buildObjectSnapshot, fetchObjectSummary, setObjectConfigCollapsed]);

  // Cross Matrix shares the object snapshot and adds the variant grouping mode.
  const appliedCrossConfigRef = useRef(null);
  const buildCrossSnapshot = useCallback(
    () => ({ ...buildObjectSnapshot(), variantMode }),
    [buildObjectSnapshot, variantMode],
  );
  const applyCrossSummary = useCallback(async () => {
    const snapshot = buildCrossSnapshot();
    appliedCrossConfigRef.current = snapshot;
    const ok = await fetchCrossSummary(snapshot);
    if (ok) setObjectConfigCollapsed(true);
    return ok;
  }, [buildCrossSnapshot, fetchCrossSummary, setObjectConfigCollapsed]);

  // Register the persisted settings for the centralised dashboard config.
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title,
      analysisType,
      variantObjectType,
      variantMode,
      objectType,
      groupByAttribute,
      abcMetric,
      abcAggregation,
      dateField,
      dateFieldOrigin,
      xyzBasis,
      xyzGranularity,
      abcPeriod,
      fiscalYearStartMonth,
      objectConfigCollapsed,
    }));
  }, [
    title,
    analysisType,
    variantObjectType,
    variantMode, // eslint-disable-line react-hooks/exhaustive-deps
    objectType,
    groupByAttribute,
    abcMetric,
    abcAggregation,
    dateField,
    dateFieldOrigin,
    xyzBasis,
    xyzGranularity,
    abcPeriod,
    fiscalYearStartMonth,
    objectConfigCollapsed,
  ]);

  // Keep the parent tile header in sync with the (possibly restored) title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => {
    onTitleChangeRef.current = onTitleChange;
  });
  useEffect(() => {
    onTitleChangeRef.current?.(title || DEFAULT_TITLE);
  }, [title]);

  // Variant mode auto-loads/reloads whenever its configuration changes —
  // results are cheap to serve thanks to backend result caching.
  useEffect(() => {
    if (analysisType !== ANALYSIS_TYPE_VARIANT) return;
    if (!activeFile || !variantObjectType) return;
    fetchVariantSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, activeFile, variantObjectType, variantMode]);

  // Object and Cross modes need the dropdown metadata once per dataset.
  useEffect(() => {
    if (
      analysisType !== ANALYSIS_TYPE_OBJECT &&
      analysisType !== ANALYSIS_TYPE_CROSS
    )
      return;
    if (!activeFile || configOptions) return;
    fetchConfigOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, activeFile, configOptions]);

  // Re-run a restored, complete object configuration once (e.g. after
  // reload). Only restored state qualifies: as soon as the user touches any
  // config control, results are strictly Apply-driven (the interaction flag
  // below is set by every config handler).
  const objectAutoFetchedRef = useRef(false);
  const crossAutoFetchedRef = useRef(false);
  const userConfiguredObjectRef = useRef(false);
  useEffect(() => {
    objectAutoFetchedRef.current = false;
    crossAutoFetchedRef.current = false;
    userConfiguredObjectRef.current = false;
    appliedObjectConfigRef.current = null;
    appliedCrossConfigRef.current = null;
  }, [activeFile]);
  useEffect(() => {
    if (analysisType !== ANALYSIS_TYPE_OBJECT || objectAutoFetchedRef.current)
      return;
    if (userConfiguredObjectRef.current) return;
    if (!objectType || !groupByAttribute || !dateField || objectSummary) return;

    objectAutoFetchedRef.current = true;
    appliedObjectConfigRef.current = buildObjectSnapshot();
    fetchObjectSummary(appliedObjectConfigRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, objectType, groupByAttribute, dateField, objectSummary]);

  // Cross mode mirrors object mode: re-run a restored, complete configuration
  // once; after any manual edit results are strictly Apply-driven.
  useEffect(() => {
    if (analysisType !== ANALYSIS_TYPE_CROSS || crossAutoFetchedRef.current)
      return;
    if (userConfiguredObjectRef.current) return;
    if (!objectType || !groupByAttribute || !dateField || crossSummary) return;

    crossAutoFetchedRef.current = true;
    appliedCrossConfigRef.current = buildCrossSnapshot();
    fetchCrossSummary(appliedCrossConfigRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, objectType, groupByAttribute, dateField, crossSummary]);

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    if (analysisType === ANALYSIS_TYPE_VARIANT) {
      if (variantObjectType) fetchVariantSummary();
    } else if (analysisType === ANALYSIS_TYPE_CROSS) {
      if (appliedCrossConfigRef.current)
        fetchCrossSummary(appliedCrossConfigRef.current);
    } else if (appliedObjectConfigRef.current) {
      fetchObjectSummary(appliedObjectConfigRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  useEffect(() => {
    if (!onRefresh) return;
    onRefresh(async () => {
      if (analysisType === ANALYSIS_TYPE_VARIANT) {
        if (variantObjectType) await fetchVariantSummary();
      } else if (analysisType === ANALYSIS_TYPE_CROSS) {
        if (appliedCrossConfigRef.current)
          await fetchCrossSummary(appliedCrossConfigRef.current);
      } else if (appliedObjectConfigRef.current) {
        await fetchObjectSummary(appliedObjectConfigRef.current);
      }
    });
  }, [
    onRefresh,
    analysisType,
    variantObjectType,
    fetchVariantSummary,
    fetchObjectSummary,
    fetchCrossSummary,
  ]);

  const handleOpenSettings = useCallback(
    () => setIsSettingsModalOpen(true),
    [],
  );

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Open settings"
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">Settings</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  const handleApplySettings = useCallback(
    async (values) => {
      if (onTitleChange) onTitleChange(values.title);
      setTitle(values.title);
      setVariantObjectType(values.variantObjectType);
      setVariantMode(values.variantMode);
      if (analysisType === ANALYSIS_TYPE_VARIANT && values.variantObjectType) {
        await fetchVariantSummary({
          variantObjectType: values.variantObjectType,
          variantMode: values.variantMode,
        });
      }
    },
    [
      analysisType,
      fetchVariantSummary,
      onTitleChange,
      setTitle,
      setVariantMode,
      setVariantObjectType,
    ],
  );

  // Object config handlers — changing the object type resets the dependent
  // selections to dataset-derived defaults (process start as date field).
  const handleObjectTypeChange = useCallback(
    (nextType) => {
      userConfiguredObjectRef.current = true;
      setObjectType(nextType);
      setGroupByAttribute("");
      setAbcMetric(COUNT_METRIC);
      setAbcAggregation("sum");
      setXyzBasis("count");
      const processStart = configOptions?.process_start_field?.name || "";
      setDateField(nextType ? processStart : "");
      setDateFieldOrigin(nextType || "");
    },
    [
      configOptions,
      setAbcAggregation,
      setAbcMetric,
      setDateField,
      setDateFieldOrigin,
      setGroupByAttribute,
      setObjectType,
      setXyzBasis,
    ],
  );

  const handleDateFieldChange = useCallback(
    (field, origin) => {
      userConfiguredObjectRef.current = true;
      setDateField(field);
      setDateFieldOrigin(origin);
    },
    [setDateField, setDateFieldOrigin],
  );

  const handleAbcMetricChange = useCallback(
    (metric) => {
      userConfiguredObjectRef.current = true;
      setAbcMetric(metric);
      if (metric === COUNT_METRIC) {
        setAbcAggregation("sum");
        setXyzBasis("count");
      }
    },
    [setAbcAggregation, setAbcMetric, setXyzBasis],
  );

  const handleGroupByAttributeChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setGroupByAttribute(value);
    },
    [setGroupByAttribute],
  );

  const handleAbcAggregationChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setAbcAggregation(value);
    },
    [setAbcAggregation],
  );

  const handleXyzBasisChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setXyzBasis(value);
    },
    [setXyzBasis],
  );

  const handleXyzGranularityChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setXyzGranularity(value);
    },
    [setXyzGranularity],
  );

  const handleAbcPeriodChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setAbcPeriod(value);
    },
    [setAbcPeriod],
  );

  const handleFiscalYearStartMonthChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setFiscalYearStartMonth(value);
    },
    [setFiscalYearStartMonth],
  );

  // Cross mode edits the variant grouping mode inline; like the other object
  // config controls it marks the config as user-touched so results stay
  // Apply-driven afterwards.
  const handleCrossVariantModeChange = useCallback(
    (value) => {
      userConfiguredObjectRef.current = true;
      setVariantMode(value);
    },
    [setVariantMode],
  );

  const applyDisabledReason = !objectType
    ? "Select an object type to configure the analysis."
    : !groupByAttribute
      ? "Select a Group By attribute — groups (e.g. customers, campaigns) are what gets classified."
      : !dateField
        ? "Select a date field for the stability measurement."
        : null;

  const handleCellClick = useCallback((cell) => {
    // Prepared for future dashboard filtering — the ids are already here.
    console.debug(
      `[ABC/XYZ] Cell ${cell.abc_xyz_class} clicked:`,
      cell.ids.length,
      "items,",
      cell.object_ids.length,
      "object ids",
    );
  }, []);

  const handleCrossCellClick = useCallback((cell) => {
    // Drill-down stub — the cross cell already carries case, variant and group
    // ids for future dashboard filtering / export.
    console.debug(
      `[ABC/XYZ] Cross cell ${cell.cross_class} (${cell.decision}) clicked:`,
      cell.object_ids.length,
      "cases,",
      cell.variant_ids.length,
      "variants,",
      cell.group_keys.length,
      "groups",
    );
  }, []);

  const isCross = analysisType === ANALYSIS_TYPE_CROSS;
  const isObjectConfigMode = analysisType === ANALYSIS_TYPE_OBJECT || isCross;
  const activeSummary =
    analysisType === ANALYSIS_TYPE_VARIANT
      ? variantSummary
      : isCross
        ? crossSummary
        : objectSummary;
  const showVariantPrompt =
    analysisType === ANALYSIS_TYPE_VARIANT && !variantObjectType && !loading;
  const showObjectPrompt =
    isObjectConfigMode && !activeSummary && !loading && !error;
  const applyActiveSummary = isCross ? applyCrossSummary : applyObjectSummary;

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="sm" grow>
        {/* Toggle + KPI row share one card, matching the visual weight of
            the Object/Cross config panel below (same bg-gray-50/border/
            rounded-lg/p-3 treatment) so the tile reads as clearly bounded
            sections rather than loose controls floating on the tile body. */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2 no-drag">
          <AnalysisTypeToggle
            value={analysisType}
            onChange={setAnalysisType}
            disabled={loading}
          />
          {/* Flex-wrap rather than a grid: an auto-fit grid collapses its empty
              tracks and stretches three cards across the whole tile, which is
              exactly the wasted width this layout avoids. Bounded cards stay
              compact, sit left-aligned and wrap only when genuinely short of
              room — the same stat-row pattern LeadTimeStatistics uses. Gap
              matches the config panel's field grid (gap-2) rather than the
              wider gap-3, so the KPI row doesn't read looser than the rest
              of the tile's spacing scale. */}
          <div className="flex flex-wrap gap-2 no-drag">
            {!error && (
              <ABCXYZKpiCards
                mode={analysisType}
                summary={activeSummary}
                // Before a result exists the card set follows the CONFIGURED
                // metric, so applying a numeric one does not add a fourth card
                // and reflow the row.
                abcMetricName={
                  analysisType === ANALYSIS_TYPE_OBJECT
                    ? (activeSummary?.abc_metric_name ?? abcMetric)
                    : COUNT_METRIC
                }
              />
            )}
          </div>
        </div>
        {isObjectConfigMode && (
          <ObjectConfigPanel
            configOptions={configOptions}
            objectType={objectType}
            groupByAttribute={groupByAttribute}
            abcMetric={abcMetric}
            abcAggregation={abcAggregation}
            dateField={dateField}
            dateFieldOrigin={dateFieldOrigin}
            xyzBasis={xyzBasis}
            xyzGranularity={xyzGranularity}
            abcPeriod={abcPeriod}
            fiscalYearStartMonth={fiscalYearStartMonth}
            onObjectTypeChange={handleObjectTypeChange}
            onGroupByAttributeChange={handleGroupByAttributeChange}
            onAbcMetricChange={handleAbcMetricChange}
            onAbcAggregationChange={handleAbcAggregationChange}
            onDateFieldChange={handleDateFieldChange}
            onXyzBasisChange={handleXyzBasisChange}
            onXyzGranularityChange={handleXyzGranularityChange}
            onAbcPeriodChange={handleAbcPeriodChange}
            onFiscalYearStartMonthChange={handleFiscalYearStartMonthChange}
            onApply={applyActiveSummary}
            loading={loading}
            applyDisabledReason={applyDisabledReason}
            // Collapse is only meaningful once there is something to look
            // at. The flag is shared by Object and Cross, so without this a
            // mode that has not been applied yet would hide its own Apply
            // button behind a collapsed panel.
            collapsed={objectConfigCollapsed && Boolean(activeSummary)}
            onToggleCollapsed={() =>
              setObjectConfigCollapsed(!objectConfigCollapsed)
            }
            title={`${ANALYSIS_TYPE_LABELS[analysisType]} Configuration`}
            showVariantMode={isCross}
            variantMode={variantMode}
            onVariantModeChange={handleCrossVariantModeChange}
          />
        )}

        {showVariantPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Variant Matrix"
            message="Open settings to choose the object type whose variants should be classified."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Open Settings
              </button>
            }
          />
        )}

        {showObjectPrompt && !applyDisabledReason && (
          <TileStatusContent
            status="info"
            title="Ready to analyze"
            message={
              isCross
                ? "Click Apply to build the Object × Variant cross matrix for the configured scope."
                : "Click Apply to classify the configured object groups."
            }
          />
        )}

        {activeSummary &&
          !error &&
          (isCross ? (
            <CrossMatrix
              summary={activeSummary}
              onCellClick={handleCrossCellClick}
              groupByAttribute={appliedCrossConfigRef.current?.groupByAttribute}
            />
          ) : (
            <ABCXYZMatrix
              matrixCells={activeSummary.matrix_cells}
              mode={analysisType}
              onCellClick={handleCellClick}

              groupByAttribute={
                analysisType === ANALYSIS_TYPE_OBJECT
                  ? appliedObjectConfigRef.current?.groupByAttribute
                  : undefined
              }
              objectType={
                analysisType === ANALYSIS_TYPE_OBJECT
                  ? appliedObjectConfigRef.current?.objectType
                  : variantObjectType
              }
            />
          ))}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load analysis"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <button
                onClick={() =>
                  analysisType === ANALYSIS_TYPE_VARIANT
                    ? fetchVariantSummary()
                    : applyActiveSummary()
                }
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Retry
              </button>
            }
          />
        )}
      </TileContent>

      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="ABC/XYZ Matrix Settings"
        getInitialValues={() => ({
          title,
          variantObjectType,
          variantMode,
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) => !values.variantObjectType || loading}
        applyButtonText={loading ? "Calculating…" : "Apply"}
      >
        {({ values, setField }) => (
          <ABCXYZAnalysisSettings
            title={values.title}
            objectTypes={objectTypes}
            variantObjectType={values.variantObjectType}
            variantMode={values.variantMode}
            onTitleChange={(v) => setField("title", v)}
            onVariantObjectTypeChange={(v) => setField("variantObjectType", v)}
            onVariantModeChange={(v) => setField("variantMode", v)}
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default ABCXYZAnalysisPanel;
